import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, Download, Mail, Phone } from 'lucide-react'
import { useProgram, useCoaches, useTeams } from '@/lib/hooks'
import { Button } from '@/components/ui/button'
import { Reveal } from '@/components/motion/Reveal'
import { PageHero } from '@/components/layout/PageHero'
import type { Coach } from '@/types/api'

export function ProgramDetailPage() {
  const { slug = '' } = useParams()
  const { data: program, isLoading } = useProgram(slug)
  const { data: coaches = [] } = useCoaches()
  const { data: teams } = useTeams()

  if (isLoading) return <div className="container-narrow py-24 text-muted-foreground">Loading…</div>
  if (!program) return <div className="container-narrow py-24">Program not found. <Link to="/programs" className="text-cosmos-green">← Back</Link></div>

  const relatedCoachList: Coach[] = (program.relatedCoaches ?? [])
    .map((s) => coaches.find((c) => c.slug === s))
    .filter((c): c is Coach => !!c)

  return (
    <>
      <PageHero
        eyebrow={program.ageRange}
        title={program.name}
        intro={program.tagline}
        image={program.hero ?? program.image}
        crumbs={[{ label: 'Programs', to: '/programs' }, { label: program.name }]}
        height="tall"
        gold={program.featured}
      />

      <section className="py-20">
        <div className="container-narrow">
          <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-cosmos-green mb-8">
            <ArrowLeft className="h-4 w-4" /> All programs
          </Link>

          <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr] items-start mb-16">
            <div>
              <Reveal>
                <span className="eyebrow">About the Program</span>
                <h2 className="mt-3 font-display text-3xl md:text-4xl uppercase leading-none">What you'll experience.</h2>
                <div className="mt-6 space-y-4 text-base text-muted-foreground leading-relaxed">
                  {program.description.split('\n\n').map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                {program.callout && (
                  <div className="mt-8 rounded-xl border-l-4 border-cosmos-gold bg-cosmos-gold/8 p-5">
                    <div className="font-display text-lg uppercase text-cosmos-gold-dark">"{program.callout}"</div>
                  </div>
                )}
                <div className="mt-8 flex gap-3 flex-wrap">
                  <Button asChild><Link to="/register">Register Now <ArrowRight className="h-4 w-4" /></Link></Button>
                  {program.pdf && (
                    <Button asChild variant="outline">
                      <a href={program.pdf} target="_blank" rel="noreferrer"><Download className="h-4 w-4" /> Program PDF</a>
                    </Button>
                  )}
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="rounded-2xl overflow-hidden border border-border bg-pitch-800 aspect-[4/5]">
                <img src={program.image} alt={program.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="rounded-2xl border border-border bg-card p-8 mb-12">
              <div className="eyebrow mb-4">Program Highlights</div>
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                {program.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2.5 text-sm">
                    <Check className="h-5 w-5 text-cosmos-green shrink-0 mt-0.5" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {relatedCoachList.length > 0 && (
            <Reveal>
              <div className="mb-12">
                <div className="mb-6">
                  <span className="eyebrow">Program Coaches</span>
                  <h3 className="mt-2 font-display text-2xl uppercase">Who you'll train with.</h3>
                </div>
                <div className="grid gap-5 md:grid-cols-2">
                  {relatedCoachList.map((c) => (
                    <div key={c.slug} className="rounded-2xl border border-border bg-card p-5 flex gap-4 hover:border-cosmos-green transition-colors">
                      <img src={c.image} alt={c.name} className="h-24 w-24 rounded-xl object-cover shrink-0" loading="lazy" />
                      <div className="min-w-0">
                        <div className="font-display text-lg uppercase leading-tight">{c.name}</div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{c.role}</div>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{c.bio}</p>
                        <div className="mt-3 flex gap-3 text-xs">
                          {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-cosmos-green hover:underline"><Mail className="h-3 w-3" /> Email</a>}
                          {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-cosmos-green hover:underline"><Phone className="h-3 w-3" /> {c.phone}</a>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {teams && (
            <Reveal>
              <div className="mb-12">
                <div className="mb-6">
                  <span className="eyebrow eyebrow-gold">Teams in this Pathway</span>
                  <h3 className="mt-2 font-display text-2xl uppercase">Boys & Girls squads.</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {[...teams.boys.slice(0, 4), ...teams.girls.slice(0, 4)].map((t) => (
                    <Link
                      key={t.id}
                      to="/teams"
                      className="rounded-xl overflow-hidden border border-border bg-card hover:shadow-md hover:-translate-y-0.5 transition-all"
                    >
                      <div className="aspect-video bg-pitch-800 overflow-hidden">
                        {t.image && <img src={t.image} alt={t.age} loading="lazy" className="w-full h-full object-cover" />}
                      </div>
                      <div className="p-3">
                        <div className="font-display text-sm uppercase">{t.id.startsWith('b') ? 'Boys' : 'Girls'} {t.age}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">{t.coach}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {program.gallery && program.gallery.length > 0 && (
            <Reveal>
              <div>
                <div className="mb-6">
                  <span className="eyebrow">Program Gallery</span>
                  <h3 className="mt-2 font-display text-2xl uppercase">On the pitch.</h3>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {program.gallery.map((src) => (
                    <div key={src} className="rounded-lg overflow-hidden aspect-[4/3] bg-pitch-800">
                      <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          <div className="mt-16 rounded-2xl bg-pitch-900 text-white p-8 md:p-10 text-center">
            <span className="eyebrow eyebrow-gold justify-center">Ready to start?</span>
            <h3 className="mt-3 font-display text-3xl md:text-4xl uppercase">Join the {program.name} program.</h3>
            <p className="mt-3 text-white/70 max-w-lg mx-auto">A coach will reach out within 48 hours after registration to confirm placement and next steps.</p>
            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              <Button asChild variant="gold"><Link to="/register">Register Now <ArrowRight className="h-4 w-4" /></Link></Button>
              <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10"><Link to="/contact">Contact Office</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
